import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import api from '../../utils/api';
import Badge from '../common/Badge';

const FilteredLeadSelectTable = ({ selectedLeads, setSelectedLeads, onLeadDataChange }) => {
  const [leadsData, setLeadsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const searchDebounceRef = useRef(null);

  const hasActiveFilter = searchText || filterType || filterStatus || filterSource;

  // Dropdown options
  const [leadTypes, setLeadTypes] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [typesRes, statusesRes, sourcesRes] = await Promise.all([
          api('/lead-types'),
          api('/lead-statuses'),
          api('/lead-sources')
        ]);
        if (typesRes.ok) setLeadTypes(await typesRes.json());
        if (statusesRes.ok) setLeadStatuses(await statusesRes.json());
        if (sourcesRes.ok) setLeadSources(await sourcesRes.json());
      } catch (e) {
        console.error('Error fetching lead settings:', e);
      }
    };
    fetchSettings();
  }, []);

  const fetchLeads = async ({ search = '', leadType = '', status = '', source = '', pageNum = 1, append = false } = {}) => {
    try {
      if (!append) setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (leadType) params.append('leadType', leadType);
      if (status) params.append('status', status);
      if (source) params.append('source', source);
      params.append('page', pageNum);
      params.append('limit', 100);

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await api(`/leads${query}`);
      if (response.ok) {
        const data = await response.json();
        if (append) {
          setLeadsData(prev => [...prev, ...(data.leads || [])]);
        } else {
          setLeadsData(data.leads || []);
        }
        setTotalLeads(data.total || 0);
        setPage(data.page || 1);
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and filter changes
  useEffect(() => {
    fetchLeads({ search: searchText, leadType: filterType, status: filterStatus, source: filterSource, pageNum: 1, append: false });
  }, [filterType, filterStatus, filterSource]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchLeads({ search: val, leadType: filterType, status: filterStatus, source: filterSource, pageNum: 1, append: false });
    }, 400);
  };

  const clearFilters = () => {
    setSearchText('');
    setFilterType('');
    setFilterStatus('');
    setFilterSource('');
    fetchLeads({ search: '', leadType: '', status: '', source: '', pageNum: 1, append: false });
  };

  // Whenever selectedLeads changes, optionally notify parent of the latest lead's full data
  useEffect(() => {
    if (selectedLeads.length > 0 && onLeadDataChange) {
      const latestSelectedId = selectedLeads[selectedLeads.length - 1];
      const leadData = leadsData.find(l => l._id === latestSelectedId);
      if (leadData) {
        onLeadDataChange(leadData, leadsData);
      } else {
        // If not found in current view, fetch it
        api(`/leads/${latestSelectedId}`).then(r => r.json()).then(data => {
            if (data && data._id) onLeadDataChange(data, leadsData);
        }).catch(e => console.error("Error fetching lead detail", e));
      }
    } else if (onLeadDataChange) {
      onLeadDataChange(null, leadsData);
    }
  }, [selectedLeads, leadsData, onLeadDataChange]);

  const isAllPageSelected = leadsData.length > 0 && leadsData.every(l => selectedLeads.includes(l._id));

  const toggleSelectAllPage = (e) => {
    if (e.target.checked) {
      const newSelected = new Set([...selectedLeads, ...leadsData.map(l => l._id)]);
      setSelectedLeads(Array.from(newSelected));
    } else {
      const pageIds = leadsData.map(l => l._id);
      setSelectedLeads(selectedLeads.filter(id => !pageIds.includes(id)));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedLeads.includes(id)) {
      setSelectedLeads(selectedLeads.filter(leadId => leadId !== id));
    } else {
      setSelectedLeads([...selectedLeads, id]);
    }
  };

  const selectAllMatching = async () => {
    try {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (filterType) params.append('leadType', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (filterSource) params.append('source', filterSource);
      params.append('selectAllIds', 'true');

      const response = await api(`/leads?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedLeads(data.ids || []);
      }
    } catch (e) {
      console.error("Error selecting all matching:", e);
    }
  };

  const selectStyle = {
    padding: '8px 12px',
    borderRadius: 'var(--border-radius-md)',
    border: '1px solid var(--color-border-primary)',
    fontSize: '13px',
    background: 'white',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    outline: 'none'
  };

  return (
    <div style={{ background: 'var(--color-background-primary)', borderRadius: '8px', border: '1px solid var(--color-border-primary)', display: 'flex', flexDirection: 'column' }}>
      
      {/* Filter Bar */}
      <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchText}
            onChange={handleSearchChange}
            style={{ width: '100%', padding: '8px 12px 8px 34px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', fontSize: '13px', boxSizing: 'border-box' }}
          />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectStyle}>
          <option value="">🌡️ Any Temperature</option>
          {leadTypes.map(t => <option key={t._id} value={t.name}>{t.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
          <option value="">📋 Any Stage</option>
          {leadStatuses.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
        </select>
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={selectStyle}>
          <option value="">🌍 Any Source</option>
          {leadSources.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
        </select>
        {hasActiveFilter && (
          <button onClick={clearFilters} style={{ padding: '8px 12px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', background: 'white', fontSize: '12px', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* Select All Banner */}
      {isAllPageSelected && totalLeads > leadsData.length && selectedLeads.length < totalLeads && (
        <div style={{ background: '#f0f9ff', padding: '12px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontSize: '14px' }}>
          All <b>{leadsData.length}</b> leads on this page are selected. 
          <button type="button" onClick={selectAllMatching} style={{ background: 'none', border: 'none', color: '#0284c7', fontWeight: 'bold', marginLeft: '8px', cursor: 'pointer', textDecoration: 'underline' }}>
            Select all {totalLeads} matching leads
          </button>
        </div>
      )}

      {/* Table Area */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {loading && leadsData.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading leads...</div>
        ) : leadsData.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No leads match the selected filters.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ background: 'var(--color-background-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', width: '40px' }}>
                  <input type="checkbox" checked={isAllPageSelected} onChange={toggleSelectAllPage} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                </th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>NAME</th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>PHONE</th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>STATUS</th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>SOURCE</th>
              </tr>
            </thead>
            <tbody>
              {leadsData.map(lead => (
                <tr key={lead._id} style={{ borderBottom: '1px solid var(--color-border-secondary)', background: selectedLeads.includes(lead._id) ? 'rgba(99,102,241,0.05)' : 'transparent' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <input type="checkbox" checked={selectedLeads.includes(lead._id)} onChange={() => toggleSelectOne(lead._id)} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-primary)' }}>{lead.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>{lead.propertyInterest || 'No Property Interest'}</div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>{lead.phone}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <Badge type={lead.status === 'New' ? 'Hot' : 'Cold'}>{lead.status}</Badge>
                      {lead.leadType && <Badge type={lead.leadType}>{lead.leadType}</Badge>}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>{lead.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer Area */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--color-border-primary)', background: 'var(--color-background-secondary)', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            Showing {leadsData.length} of {totalLeads} leads
          </div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--color-brand-primary)' }}>
            {selectedLeads.length} leads selected
          </div>
        </div>

        {leadsData.length < totalLeads && (
          <button
            type="button"
            onClick={() => fetchLeads({ search: searchText, leadType: filterType, status: filterStatus, source: filterSource, pageNum: page + 1, append: true })}
            disabled={loading}
            style={{ padding: '6px 16px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', background: 'white', color: 'var(--color-text-primary)', fontSize: '13px', fontWeight: '500', cursor: loading ? 'not-allowed' : 'pointer' }}
          >
            {loading ? 'Loading...' : 'Load Next 100 ↓'}
          </button>
        )}
      </div>
    </div>
  );
};

export default FilteredLeadSelectTable;
